import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { clamp, paddedDomain } from "../../shared/chartMath";
import {
  BAR_FILL_ALPHA,
  QUIET_BAR_FILL_ALPHA,
} from "../general/dataAtoms/shared.ts";
import { SettledVsLitigatedMetric } from "./SettlementNetResultChart.tsx";

interface Range {
  worst: number;
  avg: number;
  best: number;
}

// SVG geometry, in px
const WIDTH = 252;
const HEIGHT = 80;
// wide enough for the longest line of a caption ("settlement", 51 px in Inter
// at 10 px) and a gap before the labels of the bars
const CAPTION_COLUMN_WIDTH = 56;
// a caption too long for its column is given on several lines, the first
// level with the bar and the others below it
const CAPTION_LINE_HEIGHT = 11;
const PADDING_RIGHT = 12;
const BAR_HEIGHT = 8;
const Y_BEFORE = 18;
const Y_AFTER = 56;
// a narrower bar cannot carry a label at each end without them overlapping:
// the widest compact amount ("-£999.9M") is 42.5 px in Inter at 9 px, so two
// of them and a gap
const MIN_BAR_WIDTH_FOR_END_LABELS = 90;
// domain padding as a share of the span, so a bar end never sits on the edge
const DOMAIN_PAD_FRACTION = 0.06;

/**
 * Two aligned net-result number-lines for a case's tooltip: the litigated range
 * (before) over the settled range (after), each a bar from worst→best, drawn on
 * one shared scale with a dashed break-even (0) line. Analogous to the
 * bargaining SettlementRangeStrip on the quality chart.
 *
 * The marked value follows whichever difference the chart is plotting. Settling
 * routinely improves the worst case while lowering the average — that is the
 * risk-for-value trade — so a strip that always marked the average would
 * contradict the dot it belongs to whenever exposure is on screen.
 */
export const NetResultRangeStrip = ({
  before,
  after,
  metric = "netResult",
}: {
  before: Range;
  after: Range;
  metric?: SettledVsLitigatedMetric;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.settledVsLitigated",
  });

  const barLeft = CAPTION_COLUMN_WIDTH;
  const barRight = WIDTH - PADDING_RIGHT;
  const barWidth = barRight - barLeft;

  // both ranges from worst to best, and the break-even line
  const { min: domainMin, max: domainMax } = paddedDomain(
    [before.worst, after.worst, before.best, after.best, 0],
    DOMAIN_PAD_FRACTION,
  );
  const x = (v: number) =>
    barLeft + ((v - domainMin) / (domainMax - domainMin || 1)) * barWidth;
  const clampLabel = (v: number) => clamp(v, barLeft + 2, barRight - 2);

  // the end of the range the plotted metric actually compares
  const marked: keyof Range = metric === "exposure" ? "worst" : "avg";
  const isAfterHigher = after[marked] >= before[marked];
  const { main, dark } = isAfterHigher
    ? theme.palette.data.positive
    : theme.palette.data.negative;

  // the range before stands back behind the one settling led to, which takes
  // the colour of the way the compared value moved
  const rows = [
    {
      range: before,
      yCentre: Y_BEFORE,
      caption: t("stripBefore"),
      fill: alpha(theme.palette.grey[600], QUIET_BAR_FILL_ALPHA),
      line: theme.palette.grey[700],
    },
    {
      range: after,
      yCentre: Y_AFTER,
      caption: t("stripAfter"),
      fill: alpha(main, BAR_FILL_ALPHA),
      line: dark,
    },
  ];

  return (
    <svg
      role="img"
      aria-label={t("stripLabel")}
      width={WIDTH}
      height={HEIGHT}
      style={{ display: "block", marginTop: 6 }}
    >
      {/* break-even (0) reference — always on screen, since the domain is
          anchored at 0 and padded outward on both sides */}
      <line
        x1={x(0)}
        x2={x(0)}
        y1={Y_BEFORE - BAR_HEIGHT}
        y2={Y_AFTER + BAR_HEIGHT}
        stroke={theme.palette.text.secondary}
        strokeWidth={1}
        strokeDasharray="3 3"
      />
      {rows.map((row) => {
        const xWorst = x(row.range.worst);
        const xBest = x(row.range.best);
        const hasRoomForEndLabels =
          xBest - xWorst > MIN_BAR_WIDTH_FOR_END_LABELS;
        return (
          <g key={row.caption}>
            <text
              x={0}
              y={row.yCentre + 3}
              fontSize={10}
              fill={theme.palette.text.secondary}
            >
              {row.caption.split("\n").map((line, index) => (
                <tspan
                  key={index}
                  x={0}
                  dy={index === 0 ? 0 : CAPTION_LINE_HEIGHT}
                >
                  {line}
                </tspan>
              ))}
            </text>
            {/* worst→best range bar */}
            <rect
              x={xWorst}
              y={row.yCentre - BAR_HEIGHT / 2}
              width={Math.max(1, xBest - xWorst)}
              height={BAR_HEIGHT}
              fill={row.fill}
              stroke={row.line}
              strokeWidth={1}
              rx={2}
            />
            {/* tick on the compared value */}
            <line
              x1={x(row.range[marked])}
              x2={x(row.range[marked])}
              y1={row.yCentre - BAR_HEIGHT / 2 - 2}
              y2={row.yCentre + BAR_HEIGHT / 2 + 2}
              stroke={row.line}
              strokeWidth={2}
            />
            <text
              x={clampLabel(x(row.range[marked]))}
              y={row.yCentre - BAR_HEIGHT / 2 - 4}
              fontSize={9}
              fontWeight="bold"
              textAnchor="middle"
              fill={row.line}
            >
              {currency.formatCompact(row.range[marked])}
            </text>
            {/* range ends, when there is room — skipping the one the tick
                already labels */}
            {hasRoomForEndLabels && (
              <>
                {marked !== "worst" && (
                  <text
                    x={xWorst}
                    y={row.yCentre + BAR_HEIGHT / 2 + 12}
                    fontSize={9}
                    textAnchor="start"
                    fill={theme.palette.text.secondary}
                  >
                    {currency.formatCompact(row.range.worst)}
                  </text>
                )}
                <text
                  x={xBest}
                  y={row.yCentre + BAR_HEIGHT / 2 + 12}
                  fontSize={9}
                  textAnchor="end"
                  fill={theme.palette.text.secondary}
                >
                  {currency.formatCompact(row.range.best)}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
};
